import { useMemo } from "react";
import { ClientError, GraphQLClient } from "graphql-request";
import { useAuth } from "react-oidc-context";

import { usePreview, withPreview } from "@/lib/preview";

/**
 * Longest a single GraphQL attempt may take. The backend gives up on a
 * ClickHouse query after 10 s and API Gateway after 29 s: an attempt still
 * running at 20 s is stuck (typically on a cold or saturated function), and a
 * fresh attempt is the faster way to an answer.
 */
export const GRAPHQL_ATTEMPT_TIMEOUT_MS = 20_000;

/** Raised when an attempt exceeds {@link GRAPHQL_ATTEMPT_TIMEOUT_MS}. */
export class RequestTimeoutError extends Error {
  constructor() {
    super("The data service took too long to answer.");
    this.name = "RequestTimeoutError";
  }
}

/** `fetch` with a per-attempt deadline, still honouring a caller's own abort signal. */
export function fetchWithTimeout(timeoutMs = GRAPHQL_ATTEMPT_TIMEOUT_MS): typeof fetch {
  return async (input, init) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(new RequestTimeoutError()), timeoutMs);
    const callerSignal = init?.signal;
    const forwardAbort = () => controller.abort(callerSignal?.reason);
    if (callerSignal?.aborted) forwardAbort();
    else callerSignal?.addEventListener("abort", forwardAbort, { once: true });
    try {
      await Promise.resolve();
      controller.signal.throwIfAborted();
      return await fetch(input, { ...init, signal: controller.signal });
    } catch (error) {
      throw controller.signal.reason instanceof RequestTimeoutError
        ? controller.signal.reason
        : error;
    } finally {
      clearTimeout(timer);
      callerSignal?.removeEventListener("abort", forwardAbort);
    }
  };
}

/**
 * Whether trying again can help: a timeout, a dropped connection, throttling or
 * a server-side failure. A GraphQL error (bad query, forbidden tenant…) will
 * fail the same way every time and is reported at once instead.
 */
export function isTransientRequestError(error: unknown): boolean {
  if (error instanceof RequestTimeoutError) return true;
  if (error instanceof ClientError) {
    const status = error.response.status;
    return status === 408 || status === 429 || status >= 500;
  }
  // `fetch` rejects with a TypeError when the network or CORS preflight fails.
  return error instanceof TypeError;
}

/** Up to two more attempts, only for failures that another attempt can fix. */
export function retryTransientRequest(failureCount: number, error: unknown): boolean {
  return failureCount < 2 && isTransientRequestError(error);
}

/** Short, jittered pauses: long enough to land on a warm instance, short enough not to be felt. */
export function transientRetryDelay(attemptIndex: number): number {
  const base = attemptIndex === 0 ? 400 : 1_500;
  return base + Math.round(Math.random() * 300);
}

// Nexus API
export function useGraphqlClient(): GraphQLClient {
  const { user } = useAuth();
  const preview = usePreview();
  return useMemo(() => {
    const base = (import.meta.env.VITE_API_ENDPOINT as string) || window.location.origin;
    return new GraphQLClient(withPreview(`${base}/api/graphql`, preview), {
      headers: { Authorization: `Bearer ${user?.access_token ?? ""}` },
      fetch: fetchWithTimeout(),
    });
  }, [user?.access_token, preview]);
}
